import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Building2, 
  ShoppingCart, 
  Stethoscope, 
  GraduationCap,
  Briefcase,
  HeadphonesIcon,
  Users,
  TrendingUp,
  MessageSquare,
  CheckCircle,
  ArrowRight
} from 'lucide-react';
import { PublicHeader } from '@/components/common/PublicHeader';
import { PublicFooter } from '@/components/common/PublicFooter';

const USE_CASE_IMAGES = {
  'customer-support': 'https://images.unsplash.com/photo-1766066014237-00645c74e9c6?w=600&h=400&fit=crop',
  'sales-lead-generation': 'https://images.unsplash.com/photo-1770627000564-3feb36aecbcd?w=600&h=400&fit=crop',
  'ecommerce': 'https://images.unsplash.com/photo-1748346918817-0b1b6b2f9bab?w=600&h=400&fit=crop',
  'healthcare': 'https://images.unsplash.com/photo-1758691461516-7e716e0ca135?w=600&h=400&fit=crop',
  'education': 'https://images.unsplash.com/photo-1771223546326-556cd8382c48?w=600&h=400&fit=crop',
  'hr-internal': 'https://images.unsplash.com/photo-1758691462123-8a17ae95d203?w=600&h=400&fit=crop',
};

const TESTIMONIAL_IMAGES = {
  'Sarah Chen': 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=60&h=60&fit=crop&crop=face',
  'Michael Torres': 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=60&h=60&fit=crop&crop=face',
  'Emma Roberts': 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=60&h=60&fit=crop&crop=face',
  'Dr. James Wilson': 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=60&h=60&fit=crop&crop=face',
  'Prof. Linda Martinez': 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=60&h=60&fit=crop&crop=face',
  'Rachel Kim': 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=60&h=60&fit=crop&crop=face',
};

const UseCasesPage = () => {
  const navigate = useNavigate();

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.pageYOffset - 100;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const useCases = [
    {
      id: 'customer-support',
      icon: HeadphonesIcon,
      iconColor: 'text-teal-500',
      bgColor: 'bg-teal-50',
      title: 'Customer Support Automation',
      subtitle: 'Reduce support tickets by 70%',
      description: 'Deploy AI Employees that answer customer questions 24/7 using your existing knowledge base, FAQs, and documentation.',
      benefits: ['Instant answers to common questions', 'Reduce support ticket volume by up to 70%', 'Available 24/7 in multiple languages', 'Seamless handoff to human agents'],
      metrics: { label: 'Average ticket reduction', value: '70%' },
      testimonial: { quote: 'Harmonix reduced our support tickets by 65% in the first month.', author: 'Sarah Chen', role: 'Head of Support, TechFlow' }
    },
    {
      id: 'sales-lead-generation',
      icon: TrendingUp,
      iconColor: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      title: 'Sales & Lead Generation',
      subtitle: 'Qualify leads automatically',
      description: 'Engage website visitors, qualify leads through natural conversation, and capture contact information without forms.',
      benefits: ['Qualify leads through conversation', 'Capture contact info naturally', 'Book meetings automatically', 'Integrate with your CRM'],
      metrics: { label: 'Lead conversion increase', value: '3x' },
      testimonial: { quote: 'Our lead conversion tripled after implementing Harmonix AI Employees.', author: 'Michael Torres', role: 'Sales Director, GrowthCo' }
    },
    {
      id: 'ecommerce',
      icon: ShoppingCart,
      iconColor: 'text-purple-600',
      bgColor: 'bg-purple-50',
      title: 'E-commerce & Retail',
      subtitle: 'Boost sales with AI assistance',
      description: 'Help customers find products, answer questions about orders, and provide personalized recommendations.',
      benefits: ['Product recommendations', 'Order status inquiries', 'Size and fit guidance', 'Returns and exchange help'],
      metrics: { label: 'Cart abandonment reduction', value: '40%' },
      testimonial: { quote: 'Customer satisfaction scores increased 45% with Harmonix.', author: 'Emma Roberts', role: 'E-commerce Manager, StyleHub' }
    },
    {
      id: 'healthcare',
      icon: Stethoscope,
      iconColor: 'text-red-600',
      bgColor: 'bg-red-50',
      title: 'Healthcare & Medical',
      subtitle: 'Patient engagement simplified',
      description: 'Provide patients with instant answers about services, appointments, and general health information.',
      benefits: ['Appointment scheduling', 'Insurance and billing questions', 'Service information', 'HIPAA-compliant conversations'],
      metrics: { label: 'Patient satisfaction', value: '92%' },
      testimonial: { quote: 'Patients love getting instant answers to their questions.', author: 'Dr. James Wilson', role: 'Medical Director, CareFirst' }
    },
    {
      id: 'education',
      icon: GraduationCap,
      iconColor: 'text-amber-600',
      bgColor: 'bg-amber-50',
      title: 'Education & Training',
      subtitle: 'Scale student support',
      description: 'Answer student questions about courses, admissions, schedules, and campus resources around the clock.',
      benefits: ['Admissions inquiries', 'Course information', 'Campus resources', 'Student support services'],
      metrics: { label: 'Response time', value: '<1 min' },
      testimonial: { quote: 'We now handle 10x more student inquiries without additional staff.', author: 'Prof. Linda Martinez', role: 'Dean of Students, Metro University' }
    },
    {
      id: 'hr-internal',
      icon: Users,
      iconColor: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      title: 'HR & Internal Support',
      subtitle: 'Employee self-service',
      description: 'Help employees find answers about policies, benefits, PTO, and company procedures without waiting for HR.',
      benefits: ['Policy and procedure questions', 'Benefits information', 'Onboarding assistance', 'IT help desk support'],
      metrics: { label: 'HR inquiry reduction', value: '60%' },
      testimonial: { quote: 'HR team now focuses on strategic work instead of repetitive questions.', author: 'Rachel Kim', role: 'VP of People, InnovateCorp' }
    }
  ];

  const industries = [
    { name: 'SaaS & Technology',      icon: Building2,      target: 'customer-support' },
    { name: 'E-commerce & Retail',    icon: ShoppingCart,   target: 'ecommerce' },
    { name: 'Healthcare',             icon: Stethoscope,    target: 'healthcare' },
    { name: 'Education',              icon: GraduationCap,  target: 'education' },
    { name: 'Professional Services',  icon: Briefcase,      target: 'hr-internal' },
    { name: 'Financial Services',     icon: TrendingUp,     target: 'sales-lead-generation' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <PublicHeader />

      {/* Hero Section */}
      <section className="py-16 sm:py-24 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block px-4 py-1.5 bg-teal-50 text-slate-900 rounded-full text-sm font-medium mb-6 border border-teal-200">
            USE CASES
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            AI Employees for Every Industry
          </h1>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            Discover how businesses across industries use Harmonix to automate conversations, capture leads, and deliver exceptional customer experiences.
          </p>
          <p className="text-sm text-slate-500">Jump to your industry below — click any chip.</p>
        </div>
      </section>

      {/* Industries Grid — each chip is clickable and jumps to the relevant use case */}
      <section className="py-8 px-4 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap justify-center gap-3">
            {industries.map((industry) => (
              <button
                key={industry.target}
                onClick={() => scrollToSection(industry.target)}
                className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-slate-200 shadow-sm hover:border-teal-400 hover:bg-teal-50 hover:-translate-y-0.5 transition-all group cursor-pointer"
                data-testid={`industry-chip-${industry.target}`}
              >
                <industry.icon className="w-4 h-4 text-slate-500 group-hover:text-teal-600" />
                <span className="text-sm text-slate-700 group-hover:text-teal-700 font-medium">{industry.name}</span>
                <ArrowRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-teal-500 group-hover:translate-x-0.5 transition-transform" />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto space-y-16">
          {useCases.map((useCase, idx) => (
            <div id={useCase.id} key={useCase.id} className={`scroll-mt-24 flex flex-col ${idx % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-8 lg:gap-12 items-center`}>
              {/* Content */}
              <div className="flex-1 space-y-6">
                <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl ${useCase.bgColor}`}>
                  <useCase.icon className={`w-7 h-7 ${useCase.iconColor}`} />
                </div>
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">{useCase.title}</h2>
                  <p className="text-lg text-teal-500 font-medium">{useCase.subtitle}</p>
                </div>
                <p className="text-slate-600 text-base">{useCase.description}</p>
                <ul className="space-y-3">
                  {useCase.benefits.map((benefit) => (
                    <li key={benefit} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
                <Button onClick={() => navigate('/login?signup=true')} className="bg-teal-600 hover:bg-teal-600/90 group">
                  Try for This Use Case
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>

              {/* Card with Image + Testimonial */}
              <div className="flex-1 w-full max-w-md">
                <Card className="bg-white border-slate-200 shadow-lg overflow-hidden">
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={USE_CASE_IMAGES[useCase.id]} alt={useCase.title} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <CardContent className="p-6 space-y-6">
                    <div className="text-center py-3 border-b border-slate-100">
                      <div className="text-4xl font-bold text-slate-900">{useCase.metrics.value}</div>
                      <div className="text-sm text-slate-500">{useCase.metrics.label}</div>
                    </div>
                    <div className="space-y-3">
                      <MessageSquare className="w-6 h-6 text-slate-300" />
                      <p className="text-slate-600 italic text-sm">&ldquo;{useCase.testimonial.quote}&rdquo;</p>
                      <div className="flex items-center gap-3">
                        <img 
                          src={TESTIMONIAL_IMAGES[useCase.testimonial.author] || ''} 
                          alt={useCase.testimonial.author} 
                          className="w-9 h-9 rounded-full object-cover" 
                          loading="lazy" 
                        />
                        <div>
                          <div className="font-semibold text-slate-900 text-sm">{useCase.testimonial.author}</div>
                          <div className="text-xs text-slate-500">{useCase.testimonial.role}</div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Employee Spotlight — named AI Employees per industry */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-teal-50/30" data-testid="employee-spotlight">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-teal-50 border border-teal-200 text-teal-700 text-xs font-semibold mb-4">
              <Users className="w-3.5 h-3.5" />
              Employee Spotlight
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">Meet the AI Employees Behind the Magic</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Real names, real personalities. Each Harmonix AI Employee is tuned for a specific industry so customers feel like they're talking to a teammate.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: 'Ava', role: 'Frontline Support Rep', industry: 'SaaS & Tech', accent: 'from-teal-500 to-cyan-500', tagline: 'Answers tier-1 tickets in 4 seconds. Escalates the rest.', bg: 'bg-teal-50', textColor: 'text-teal-700' },
              { name: 'Marcus', role: 'Sales Qualifier', industry: 'B2B Services', accent: 'from-indigo-500 to-violet-500', tagline: 'Qualifies leads, books demos, syncs to your CRM.', bg: 'bg-indigo-50', textColor: 'text-indigo-700' },
              { name: 'Sofia', role: 'Booking Receptionist', industry: 'Clinics & Salons', accent: 'from-rose-500 to-pink-500', tagline: 'Books appointments 24/7 across WhatsApp & web.', bg: 'bg-rose-50', textColor: 'text-rose-700' },
              { name: 'Hiro', role: 'Order Concierge', industry: 'E-commerce', accent: 'from-amber-500 to-orange-500', tagline: 'Tracks orders, handles returns, upsells smartly.', bg: 'bg-amber-50', textColor: 'text-amber-700' },
              { name: 'Priya', role: 'Onboarding Coach', industry: 'EdTech & Coaching', accent: 'from-emerald-500 to-green-500', tagline: 'Guides new students through course onboarding.', bg: 'bg-emerald-50', textColor: 'text-emerald-700' },
              { name: 'Leo', role: 'Job Dispatcher', industry: 'Plumbers & HVAC', accent: 'from-sky-500 to-blue-500', tagline: 'Captures job details, dispatches the nearest tech.', bg: 'bg-sky-50', textColor: 'text-sky-700' },
            ].map((emp) => (
              <Card key={emp.name} className="bg-white border-slate-200 hover:shadow-lg transition-shadow group" data-testid={`spotlight-${emp.name.toLowerCase()}`}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${emp.accent} flex items-center justify-center text-white text-xl font-bold shadow-md group-hover:scale-105 transition-transform`}>
                      {emp.name.charAt(0)}
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">{emp.name}</h3>
                      <p className="text-sm text-slate-500">{emp.role}</p>
                    </div>
                  </div>
                  <div className={`inline-block px-2.5 py-1 rounded-md ${emp.bg} ${emp.textColor} text-xs font-semibold mb-3`}>
                    {emp.industry}
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">{emp.tagline}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-teal-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Customer Experience?
          </h2>
          <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
            Join thousands of businesses using Harmonix to automate conversations and delight customers.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => navigate('/login?signup=true')} size="lg" className="bg-teal-600 text-white font-bold hover:bg-teal-700">
              Start Free Trial
            </Button>
            <Button onClick={() => navigate('/#pricing')} size="lg" variant="outline" className="border-slate-600 text-white hover:bg-white/5">
              View Pricing
            </Button>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default UseCasesPage;
